import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getAppParams, getUrlParams } from "../apiService/timespam";
import "./Theme.css";
import "./MentalAssessment.css";
import { APIV4_BASE_URL } from "../apiService/apiClient";

const GOQII_LOGO = "https://appcdn.goqii.com/storeimg/21983_1778484667.png";

const QUESTIONS = [
  { questionId: 1, category: "Anxiety", question: "Feeling nervous, anxious, or on edge", options: ["Not at all", "Several days", "More than half the days", "Nearly every day"], scores: [0,1,2,3] },
  { questionId: 2, category: "Anxiety", question: "Not being able to stop or control worrying", options: ["Not at all", "Several days", "More than half the days", "Nearly every day"], scores: [0,1,2,3] },
  { questionId: 3, category: "Anxiety", question: "Worrying too much about different things", options: ["Not at all", "Several days", "More than half the days", "Nearly every day"], scores: [0,1,2,3] },
  { questionId: 4, category: "Anxiety", question: "Trouble relaxing", options: ["Not at all", "Several days", "More than half the days", "Nearly every day"], scores: [0,1,2,3] },
  { questionId: 5, category: "Anxiety", question: "Being so restless that it is hard to sit still", options: ["Not at all", "Several days", "More than half the days", "Nearly every day"], scores: [0,1,2,3] },
  { questionId: 6, category: "Anxiety", question: "Becoming easily annoyed or irritable", options: ["Not at all", "Several days", "More than half the days", "Nearly every day"], scores: [0,1,2,3] },
  { questionId: 7, category: "Anxiety", question: "Feeling afraid, as if something awful might happen", options: ["Not at all", "Several days", "More than half the days", "Nearly every day"], scores: [0,1,2,3] },
  { questionId: 8, category: "Depression", question: "Little interest or pleasure in doing things", options: ["Not at all", "Several days", "More than half the days", "Nearly every day"], scores: [0,1,2,3] },
  { questionId: 9, category: "Depression", question: "Feeling down, depressed, or hopeless", options: ["Not at all", "Several days", "More than half the days", "Nearly every day"], scores: [0,1,2,3] },
  { questionId: 10, category: "Depression", question: "Trouble falling or staying asleep, or sleeping too much", options: ["Not at all", "Several days", "More than half the days", "Nearly every day"], scores: [0,1,2,3] },
  { questionId: 11, category: "Depression", question: "Feeling tired or having little energy", options: ["Not at all", "Several days", "More than half the days", "Nearly every day"], scores: [0,1,2,3] },
  { questionId: 12, category: "Depression", question: "Poor appetite or overeating", options: ["Not at all", "Several days", "More than half the days", "Nearly every day"], scores: [0,1,2,3] },
  { questionId: 13, category: "Depression", question: "Feeling bad about yourself—or that you are a failure or have let yourself or your family down", options: ["Not at all", "Several days", "More than half the days", "Nearly every day"], scores: [0,1,2,3] },
  { questionId: 14, category: "Depression", question: "Trouble concentrating on things, such as reading the newspaper or watching television", options: ["Not at all", "Several days", "More than half the days", "Nearly every day"], scores: [0,1,2,3] },
  { questionId: 15, category: "Depression", question: "Moving or speaking so slowly that other people could have noticed? Or being so fidgety or restless", options: ["Not at all", "Several days", "More than half the days", "Nearly every day"], scores: [0,1,2,3] },
  { questionId: 16, category: "Depression", question: "Thoughts that you would be better off dead or of hurting yourself in some way", options: ["Not at all", "Several days", "More than half the days", "Nearly every day"], scores: [0,1,2,3] },
];

const ANXIETY_QS = ["Feeling nervous, anxious, or on edge","Not being able to stop or control worrying","Worrying too much about different things","Trouble relaxing","Being so restless that it is hard to sit still","Becoming easily annoyed or irritable","Feeling afraid, as if something awful might happen"];
const DEPRESSION_QS = ["Little interest or pleasure in doing things","Feeling down, depressed, or hopeless","Trouble falling or staying asleep, or sleeping too much","Feeling tired or having little energy","Poor appetite or overeating","Feeling bad about yourself—or that you are a failure or have let yourself or your family down","Trouble concentrating on things, such as reading the newspaper or watching television","Moving or speaking so slowly that other people could have noticed? Or the opposite—being so fidgety or restless that you have been moving around a lot more than usual","Thoughts that you would be better off dead or of hurting yourself in some way"];
const ANSWER_MAP = { 0: "Not at all", 1: "Several days", 2: "More than half the days", 3: "Nearly every day" };

const getAnxietyInfo = (s) => {
  if (s <= 4) return { label: "Minimal", color: "#66bb6a" };
  if (s <= 9) return { label: "Mild", color: "#42a5f5" };
  if (s <= 14) return { label: "Moderate", color: "#ffa726" };
  return { label: "Severe", color: "#ef5350" };
};
const getDepressionInfo = (s) => {
  if (s <= 4) return { label: "Minimal", color: "#66bb6a" };
  if (s <= 9) return { label: "Mild", color: "#42a5f5" };
  if (s <= 14) return { label: "Moderate", color: "#FBC525" };
  if (s <= 19) return { label: "Moderately Severe", color: "#ffa726" };
  return { label: "Severe", color: "#ef5350" };
};
const getWellnessInfo = (s) => {
  if (s <= 8) return { label: "Healthy Mind" };
  if (s <= 18) return { label: "Mild Concerns" };
  if (s <= 28) return { label: "Moderate Concerns" };
  if (s <= 38) return { label: "Moderately Severe Concerns" };
  return { label: "Severe Concerns" };
};

const getDisclaimerText = (a, d) => {
  if (a <= 4 && d <= 4) return "Maintain healthy lifestyle habits, including regular exercise, mindfulness, and balanced nutrition. Retake the questionnaire in two weeks to track your mental wellness.";
  if (a <= 4 && d <= 9) return "Monitor symptoms and engage in stress-reducing activities like meditation or journaling. Consider speaking with a counselor for support.";
  if (a <= 4 && d <= 14) return "Seek guidance from a mental health professional to manage symptoms. Early intervention can help prevent escalation.";
  if (a <= 4 && d <= 19) return "Professional evaluation is recommended. Consider therapy and/or medical intervention for effective symptom management.";
  if (a <= 4) return "Immediate consultation with a mental health specialist is critical. Crisis support may also be necessary.";
  if (a <= 9 && d <= 4) return "Practice relaxation techniques, track symptoms closely, and adopt stress-reducing habits. Retake the questionnaire in two weeks.";
  if (a <= 9 && d <= 9) return "Consider speaking with a mental health professional for coping strategies. Therapy may help manage mild symptoms.";
  if (a <= 9 && d <= 14) return "Early intervention with therapy is recommended. A mental health professional can help tailor strategies to your needs.";
  if (a <= 9 && d <= 19) return "Schedule a mental health evaluation. Therapy and potential medical treatment may be required for symptom management.";
  if (a <= 9) return "Immediate mental health intervention is essential. Structured treatment plans and ongoing therapy may be necessary.";
  if (a <= 14 && d <= 4) return "Consult a counselor to explore strategies for managing moderate anxiety. Retake the questionnaire in two weeks.";
  if (a <= 14 && d <= 9) return "Therapy-focused intervention like CBT is recommended to manage anxiety and depression symptoms effectively.";
  if (a <= 14 && d <= 14) return "A comprehensive mental health evaluation is recommended. Therapy and coping strategies can improve overall well-being.";
  if (a <= 14 && d <= 19) return "Begin therapy immediately. Medical intervention may also be necessary to address moderate to severe symptoms.";
  if (a <= 14) return "Urgent professional evaluation is required. Crisis intervention and structured treatment plans are critical.";
  if (d <= 4) return "Seek immediate professional support to address severe anxiety symptoms.";
  if (d <= 9) return "Therapy and possible medical evaluation are necessary. Symptoms require close monitoring and structured management.";
  if (d <= 14) return "Comprehensive mental health support is critical. Therapy and/or medication may be needed.";
  if (d <= 19) return "Immediate intervention is essential. Therapy and medical treatment should be prioritized for effective symptom relief.";
  return "Crisis intervention and urgent psychiatric care are critical. A structured, multidisciplinary treatment approach is necessary.";
};

const ColorBar = ({ score, sections }) => (
  <div className="ma-color-bar">
    {sections.map((s, i) => {
      const active = score >= s.start && score <= s.end;
      return (
        <div
          key={i}
          style={{
            flex: 1,
            backgroundColor: active ? s.color : "var(--border-subtle)",
            borderRight: i < sections.length - 1 ? "2px solid var(--bg-card)" : "none",
          }}
        />
      );
    })}
  </div>
);

const downloadPdf = (reportUrl) => {
  if (!reportUrl) return;
  const link = document.createElement("a");
  link.href = reportUrl;
  link.setAttribute("download", "report.pdf");
  link.style.display = "none";
  document.body.appendChild(link);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  if (isIOS) {
    window.open(reportUrl, "_blank");
  } else {
    link.click();
  }
  document.body.removeChild(link);
};

const MentalAssessment = () => {
  const { t } = useTranslation();
  const { theme, lang } = getUrlParams();
  const { goqiiUserId, nonce, apiKey, signature, goqiiAccessToken, appVersion, appType } = getAppParams();

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    if (lang === "ar") document.documentElement.dir = "rtl";
  }, [theme, lang]);

  const [view, setView] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [currentResult, setCurrentResult] = useState(null);

  // Quiz state
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [slideKey, setSlideKey] = useState(0);
  const [answerDisabled, setAnswerDisabled] = useState(false);
  const [quizResult, setQuizResult] = useState(null);

  const buildQueryStr = useCallback((extra = {}) => {
    return new URLSearchParams({ goqiiUserId, nonce, signature, apiKey, goqiiAccessToken, appVersion, appType, ...extra }).toString();
  }, [goqiiUserId, nonce, signature, apiKey, goqiiAccessToken, appVersion, appType]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const qs = buildQueryStr();
        const res = await fetch(`${APIV4_BASE_URL}/lab/fetch_mental_health_assessment?${qs}`);
        const json = await res.json();
        if (json.data?.code === 200) {
          const formatted = json.data.assessmentData.map((item) => ({
            date: item.createdTime,
            anxietyScore: parseInt(item.anxietyScore, 10),
            depressionScore: parseInt(item.depressionScore, 10),
            wellnessScore: parseInt(item.mentalWellnessScore, 10),
            anxietyLabel: item.anxietyLabel,
            depressionLabel: item.depressionLabel,
            wellnessLabel: item.mentalWellnessLabel,
            reportUrl: item.reportUrl,
            personalDetails: { name: `${json.data.firstName} ${json.data.lastName}`, age: json.data.age, gender: json.data.gender },
            questions: item.questions,
          }));
          setResults(formatted);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [buildQueryStr]);

  const handleAnswerChange = (optionIdx) => {
    if (answerDisabled) return;
    setAnswerDisabled(true);
    const newAnswers = { ...answers, [currentIdx]: optionIdx };
    setAnswers(newAnswers);
    setTimeout(() => setAnswerDisabled(false), 500);
    if (currentIdx < QUESTIONS.length - 1) {
      setTimeout(() => {
        setCurrentIdx((p) => p + 1);
        setSlideKey((k) => k + 1);
      }, 350);
    }
  };

  const handlePrevious = () => {
    if (currentIdx === 0) return;
    setCurrentIdx((p) => p - 1);
    setSlideKey((k) => k + 1);
  };

  const calcScore = (category) =>
    Object.keys(answers)
      .filter((i) => QUESTIONS[i].category === category)
      .reduce((sum, i) => sum + QUESTIONS[i].scores[answers[i]], 0);

  const handleFinish = () => {
    const anxietyScore = calcScore("Anxiety");
    const depressionScore = calcScore("Depression");
    const mentalWellnessScore = anxietyScore + depressionScore;
    const aInfo = getAnxietyInfo(anxietyScore);
    const dInfo = getDepressionInfo(depressionScore);
    const wInfo = getWellnessInfo(mentalWellnessScore);

    const preparedQuestions = Object.keys(answers).map((key) => ({
      questionId: QUESTIONS[key].questionId,
      question: QUESTIONS[key].question,
      answer: QUESTIONS[key].scores[answers[key]],
      score: QUESTIONS[key].scores[answers[key]],
      category: QUESTIONS[key].category,
    }));

    const scoreData = { anxietyScore, depressionScore, mentalWellnessScore, anxietyLabel: aInfo.label, depressionLabel: dInfo.label, mentalWellnessLabel: wInfo.label };

    const qs = buildQueryStr({ questions: JSON.stringify(preparedQuestions), scoreData: JSON.stringify(scoreData) });
    fetch(`${APIV4_BASE_URL}/lab/save_mental_health_assessment?${qs}`).catch(console.error);

    setQuizResult({ anxietyScore, depressionScore, mentalWellnessScore, anxietyLabel: aInfo.label, depressionLabel: dInfo.label, wellnessLabel: wInfo.label });
    setView("result");
  };

  const startQuiz = () => {
    setCurrentIdx(0);
    setAnswers({});
    setSlideKey(0);
    setQuizResult(null);
    setView("quiz");
  };

  const progress = ((currentIdx + 1) / QUESTIONS.length) * 100;
  const canFinish = answers[currentIdx] !== undefined && currentIdx === QUESTIONS.length - 1;

  if (loading) {
    return (
      <div className="ma-wrapper">
        {[1, 2, 3].map((i) => <div key={i} className="ma-shimmer-card" />)}
      </div>
    );
  }

  /* ---- QUIZ VIEW ---- */
  if (view === "quiz") {
    const q = QUESTIONS[currentIdx];
    return (
      <div className="ma-wrapper">
        <p className="ma-quiz-intro">{t("mentalAssessment.quizIntro")}</p>
        <div key={slideKey} className="ma-question-card">
          <p className="ma-question-num">{t("mentalAssessment.question")} {currentIdx + 1} / {QUESTIONS.length}</p>
          <p className="ma-question-text">{q.question}</p>
          <div className="ma-options">
            {q.options.map((opt, i) => (
              <label
                key={i}
                className={`ma-option${answers[currentIdx] === i ? " selected" : ""}`}
                onClick={() => handleAnswerChange(i)}
              >
                <input type="radio" name={`q${currentIdx}`} checked={answers[currentIdx] === i} onChange={() => {}} />
                <span className="ma-option-label">{opt}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="ma-quiz-footer">
          <button className="ma-btn-outline" onClick={handlePrevious} disabled={currentIdx === 0}>{t("mentalAssessment.previous")}</button>
          <div className="ma-progress-wrap">
            <div className="ma-progress-bar" style={{ width: `${progress}%` }} />
          </div>
          {canFinish && (
            <button className="ma-btn-contained" onClick={handleFinish}>{t("mentalAssessment.finish")}</button>
          )}
        </div>
      </div>
    );
  }

  /* ---- RESULT VIEW ---- */
  if (view === "result" && quizResult) {
    const aInfo = getAnxietyInfo(quizResult.anxietyScore);
    const dInfo = getDepressionInfo(quizResult.depressionScore);
    return (
      <div className="ma-wrapper">
        <div className="ma-result-modal">
          <p className="ma-result-title">{t("mentalAssessment.yourResults")}</p>

          <p className="ma-section-label">{t("mentalAssessment.anxietyRisk")}</p>
          <p className="ma-score-text" style={{ color: aInfo.color }}>{aInfo.label} ({t("mentalAssessment.score")}: {quizResult.anxietyScore})</p>
          <ColorBar score={quizResult.anxietyScore} sections={[
            { start: 0, end: 5, color: "#66bb6a" }, { start: 6, end: 10, color: "#42a5f5" },
            { start: 11, end: 15, color: "#ffa726" }, { start: 16, end: 21, color: "#ef5350" },
          ]} />
          <div className="ma-bar-labels"><span>{t("mentalAssessment.none")} (0-4)</span><span>{t("mentalAssessment.severe")} (15-21)</span></div>
          <p className="ma-bar-note">{t("mentalAssessment.symptomsNote")} {aInfo.label.toLowerCase()}.</p>

          <hr className="ma-divider" />

          <p className="ma-section-label">{t("mentalAssessment.depressionRisk")}</p>
          <p className="ma-score-text" style={{ color: dInfo.color }}>{dInfo.label} ({t("mentalAssessment.score")}: {quizResult.depressionScore})</p>
          <ColorBar score={quizResult.depressionScore} sections={[
            { start: 0, end: 5, color: "#66bb6a" }, { start: 6, end: 10, color: "#42a5f5" },
            { start: 11, end: 15, color: "#FBC525" }, { start: 16, end: 20, color: "#ffa726" },
            { start: 21, end: 27, color: "#ef5350" },
          ]} />
          <div className="ma-bar-labels"><span>{t("mentalAssessment.none")} (0-4)</span><span>{t("mentalAssessment.severe")} (20-27)</span></div>
          <p className="ma-bar-note">{t("mentalAssessment.symptomsNote")} {dInfo.label.toLowerCase()}.</p>

          <div style={{ textAlign: "center", marginTop: 16 }}>
            <button className="ma-btn-primary" style={{ width: "auto", padding: "10px 32px" }} onClick={() => { setView("dashboard"); window.location.reload(); }}>{t("mentalAssessment.close")}</button>
          </div>
        </div>
      </div>
    );
  }

  /* ---- REPORT VIEW ---- */
  if (view === "report" && currentResult) {
    const disclaimerText = getDisclaimerText(currentResult.anxietyScore, currentResult.depressionScore);
    return (
      <div className="ma-wrapper" style={{ paddingBottom: 80 }}>
        <div className="ma-report">
          <div className="ma-report-header">
            <img src="https://appcdn.goqii.com/storeimg/21983_1778484667.png" alt="GOQii" className="ma-report-logo" />
          </div>
          <p className="ma-report-title">{t("mentalAssessment.title")}</p>
          <p className="ma-report-date">{t("mentalAssessment.dateOfAssessment")}: {currentResult.date}</p>
          <hr className="ma-report-divider" />

          <p className="ma-report-section-title">{t("mentalAssessment.personalDetails")}</p>
          <p className="ma-report-row"><strong>{t("mentalAssessment.name")}:</strong> {currentResult.personalDetails.name}</p>
          <p className="ma-report-row"><strong>{t("mentalAssessment.age")}:</strong> {currentResult.personalDetails.age}</p>
          <p className="ma-report-row"><strong>{t("mentalAssessment.gender")}:</strong> {currentResult.personalDetails.gender}</p>
          <hr className="ma-report-divider" />

          <p className="ma-report-section-title">{t("mentalAssessment.mentalWellness")}</p>
          <div className="ma-score-bar-nums">
            {["8","18","28","38","48"].map((n) => <div key={n} className="ma-score-bar-num">{n}</div>)}
          </div>
          <div className="ma-wellness-bar">
            {[["#008000","Healthy Mind"],["#4a90e2","Mild Concerns"],["#f5a623","Moderate Concerns"],["#FBC525","Mod. Severe"]].map(([c,l],i) => (
              <div key={i} className={`ma-wellness-seg${i===3?" dark-text":""}`} style={{ backgroundColor: c }}>{l}</div>
            ))}
            <div className="ma-wellness-seg" style={{ backgroundColor: "#d0021b" }}>Severe</div>
          </div>
          <p className="ma-report-row"><strong>{t("mentalAssessment.score")}:</strong> {currentResult.wellnessScore} (out of 48)</p>
          <p className="ma-report-row"><strong>{t("mentalAssessment.result")}:</strong> {currentResult.wellnessLabel}</p>
          <hr className="ma-report-divider" />

          <p className="ma-report-note">Your answers indicate you may be experiencing symptoms of {currentResult.anxietyLabel} Anxiety, {currentResult.depressionLabel} Depression and {currentResult.wellnessLabel} at the time of assessment.</p>
          <hr className="ma-report-divider" />

          <p className="ma-report-section-title">{t("mentalAssessment.recommendedAction")}:</p>
          <p className="ma-report-small">{disclaimerText}</p>

          <div className="ma-gradient-divider" />

          <p className="ma-report-section-title">Anxiety Risk (GAD-7)</p>
          <p className="ma-report-row"><strong>{t("mentalAssessment.result")}: ({currentResult.anxietyScore} out of 21) - {currentResult.anxietyLabel}</strong></p>
          {ANXIETY_QS.map((q, i) => (
            <div key={i} className="ma-report-qa">
              <p><strong>Q. {i + 1}:</strong> {q}</p>
              <p><strong>{currentResult.questions?.[i] ? ANSWER_MAP[currentResult.questions[i].answer] : "-"}</strong></p>
            </div>
          ))}
          <hr className="ma-report-divider" />

          <p className="ma-report-section-title">Depression Risk (PHQ-9)</p>
          <p className="ma-report-row"><strong>{t("mentalAssessment.result")}: ({currentResult.depressionScore} out of 27) - {currentResult.depressionLabel}</strong></p>
          {DEPRESSION_QS.map((q, i) => (
            <div key={i} className="ma-report-qa">
              <p><strong>Q. {i + 8}:</strong> {q}</p>
              <p><strong>{currentResult.questions?.[i + 7] ? ANSWER_MAP[currentResult.questions[i + 7].answer] : "-"}</strong></p>
            </div>
          ))}
          <hr className="ma-report-divider" />

          <p className="ma-report-disclaimer"><strong>Disclaimer:</strong> This assessment is based on the GAD-7 for anxiety and the PHQ-9 for depression and is intended for informational purposes only. It is not a diagnosis. If your results indicate moderate to severe levels of concern, please consult a qualified mental health professional for further evaluation.</p>

          <div className="ma-gradient-divider" />

          <div className="ma-report-actions">
            <button className="ma-btn-outline" onClick={() => setView("dashboard")}>{t("mentalAssessment.close")}</button>
            <button className="ma-btn-primary" style={{ width: "auto" }} onClick={() => downloadPdf(currentResult.reportUrl)}>{t("mentalAssessment.downloadPdf")}</button>
          </div>
        </div>
      </div>
    );
  }

  /* ---- DASHBOARD VIEW ---- */
  return (
    <div className="ma-wrapper">
      <div className="ma-hero">
        <img src="https://appcdn.goqii.com/storeimg/50700_1778484736.png" alt="Mental Wellness" className="ma-hero-img" />
      </div>
      <p className="ma-desc">{t("mentalAssessment.desc")}</p>
      <button className="ma-btn-primary ma-full-btn" onClick={startQuiz}>{t("mentalAssessment.startBtn")}</button>

      <hr className="ma-divider" />

      <p className="ma-section-title">{t("mentalAssessment.previousAssessments")}</p>

      {results.length === 0 ? (
        <p className="ma-empty-text">{t("mentalAssessment.noAssessments")}</p>
      ) : (
        results.map((r, idx) => {
          const aInfo = getAnxietyInfo(r.anxietyScore);
          const dInfo = getDepressionInfo(r.depressionScore);
          const wInfo = getWellnessInfo(r.wellnessScore);
          return (
            <div key={idx} className="ma-card" onClick={() => { setCurrentResult(r); setView("report"); }}>
              <p className="ma-card-date">{r.date}</p>
              <div className="ma-card-row">
                <span className="ma-card-label">{t("mentalAssessment.anxietyRisk")}</span>
                <span className="ma-card-value">{aInfo.label}</span>
              </div>
              <div className="ma-card-row">
                <span className="ma-card-label">{t("mentalAssessment.depressionRisk")}</span>
                <span className="ma-card-value">{dInfo.label}</span>
              </div>
              <div className="ma-card-row">
                <span className="ma-card-label">{t("mentalAssessment.mentalWellness")}</span>
                <span className="ma-card-value">{wInfo.label}</span>
              </div>
              <div style={{ textAlign: "center", marginTop: 10 }}>
                <button className="ma-btn-outline" onClick={(e) => { e.stopPropagation(); setCurrentResult(r); setView("report"); }}>{t("mentalAssessment.viewReport")}</button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default MentalAssessment;
